import { Directive, ElementRef, HostListener, Input, OnInit, Optional, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { FeatureCode } from './feature-code';
import { ModalPopupService } from '@app/main/cablepro/modal-popup.service';
import { Router } from '@node_modules/@angular/router';
import { NgModel } from '@node_modules/@angular/forms';

@Directive({
  selector: '[appPermission]'
})
export class PermissionDirective implements OnInit {

  @Input('appPermission') featureCode!: FeatureCode;
  @Input() appPermissionMode: 'hide' | 'disable' | 'alert' = 'alert'; // default
  @Input() appPermissionAlertTitle?: string;
  @Input() appPermissionAlertMessage?: string;
  @Input() addCrown?: boolean = false;

  private isStructural = false;
  private allowed = false;
  private nativeEl: any;
  private previousValue: any;

  constructor(
    @Optional() private tpl: TemplateRef<any>,
    private vcr: ViewContainerRef,
    private el: ElementRef,
    private renderer: Renderer2,
    private _appSessionService: AppSessionService,
    @Optional() private ngModel: NgModel
  ) {
    this.isStructural = !!tpl;
  }

  ngOnInit() {
    this.allowed = this._appSessionService.hasPermission(this.featureCode);

    if (this.appPermissionMode === 'hide') {
      if (this.isStructural) {
        this.vcr.clear();
        if (this.allowed) {
          this.vcr.createEmbeddedView(this.tpl);
        }
      } else {
        if (!this.allowed) {
          this.renderer.setStyle(this.el.nativeElement, 'display', 'none');
        }
      }
    }

    if (this.appPermissionMode === 'disable' && !this.allowed) {
      this.renderer.setProperty(this.el.nativeElement, 'disabled', true);
      this.renderer.setStyle(this.el.nativeElement, 'cursor', 'not-allowed');
      this.renderer.setAttribute(
        this.el.nativeElement,
        'title',
        this._appSessionService.getErrorMessage(this.featureCode)
      );
    }

    if (this.appPermissionMode === 'alert' && !this.allowed) {
      // store initial value
      this.previousValue = this.getCurrentValue();
    }

    if (this.addCrown == true) {
      this.addCrownIcon();
    }
  }

  private getCurrentValue(): any {
    if (this.ngModel) {
      return this.ngModel.model;
    }

    const attrValue = this.el.nativeElement.getAttribute('value');
    if (attrValue !== null) {
      return attrValue;
    }

    return this.el.nativeElement.value;
  }

  @HostListener('click', ['$event'])
  handleClick(event: Event) {
    const tag = this.el.nativeElement.tagName.toLowerCase();

    // Ignore form controls (they are handled by input/change/ngModelChange)
    if (['input', 'textarea', 'select', 'ng-select'].includes(tag)) {
      return;
    }
    if (this.appPermissionMode === 'alert' && !this.allowed) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      if (this.ngModel)
        this.ngModel.control.setValue(this.previousValue, { emitEvent: false });
      this.showAlert();
    }
    else if (this.ngModel) {
      // update previousValue if allowed
      this.previousValue = this.ngModel.model;
    }
  }

  @HostListener('change', ['$event'])
  handleNgSelectRawChange(event: any) {
    if (this.el.nativeElement.tagName.toLowerCase() === 'ng-select') {
      if (this.appPermissionMode === 'alert' && !this.allowed) {
        // revert UI
        if (this.ngModel) {
          this.ngModel.control.setValue(this.previousValue, { emitEvent: false });
        }

        this.showAlert();
      }
    }
  }

  // @HostListener('ngModelChange', ['$event'])
  // handleModelChange(newValue: any) {
  //   if (this.appPermissionMode === 'alert' && !this.allowed) {
  //     // revert to old value
  //     if (this.ngModel)
  //       this.ngModel.control.setValue(this.previousValue, { emitEvent: false });
  //     this.showAlert();
  //   } else {
  //     this.previousValue = newValue;
  //   }
  // }

  @HostListener('input', ['$event'])
  handleInput(event: Event) {
    if (this.appPermissionMode === 'alert' && !this.allowed) {
      const oldValue = this.previousValue ?? '';

      if (this.ngModel) {
        // revert ngModel
        this.ngModel.control.setValue(oldValue, { emitEvent: false });
      } else {
        // revert DOM value
        setTimeout(() => {
          this.el.nativeElement.value = oldValue;
        });

      }

      // stop event bubbling
      event.stopImmediatePropagation();
      event.stopPropagation();

      this.showAlert();
    } else {
      // store latest valid value
      this.previousValue = this.getCurrentValue();
    }
  }

  private showAlert() {
    if (this.appPermissionAlertTitle || this.appPermissionAlertMessage) {
      this._appSessionService.showUpgradeAlert(
        this.appPermissionAlertTitle,
        this.appPermissionAlertMessage
      );
    } else {
      // fallback to default implementation
      this._appSessionService.showUpgradeAlert();
    }
  }

  private addCrownIcon() {
    const host = this.el.nativeElement as HTMLElement;

    if (host.querySelector('.permission-crown')) return;

    if (getComputedStyle(host).position === 'static') {
      this.renderer.setStyle(host, 'position', 'relative');
    }

    // wrapper span
    const wrapper = this.renderer.createElement('span');
    this.renderer.addClass(wrapper, 'permission-crown');

    // style wrapper (circle background)
    this.renderer.setStyle(wrapper, 'position', 'absolute');
    this.renderer.setStyle(wrapper, 'top', '-5px');
    this.renderer.setStyle(wrapper, 'right', '0px');
    this.renderer.setStyle(wrapper, 'width', '18px');
    this.renderer.setStyle(wrapper, 'height', '18px');
    this.renderer.setStyle(wrapper, 'border-radius', '50%');
    this.renderer.setStyle(wrapper, 'background', 'rgb(255 215 0 / 23%)'); // gold-ish
    this.renderer.setStyle(wrapper, 'display', 'flex');
    this.renderer.setStyle(wrapper, 'align-items', 'center');
    this.renderer.setStyle(wrapper, 'justify-content', 'center');
    this.renderer.setStyle(wrapper, 'pointer-events', 'none');

    // actual icon
    const img = this.renderer.createElement('img');
    this.renderer.setAttribute(img, 'src', 'assets/cablepro/img/crown-solid.svg');
    this.renderer.setStyle(img, 'width', '10px');
    this.renderer.setStyle(img, 'height', '10px');
    if (this.allowed)
      this.renderer.setStyle(img, 'filter', 'grayscale(100%) brightness(0.8)');

    this.renderer.appendChild(wrapper, img);
    this.renderer.appendChild(host, wrapper);
  }



}
